import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { getHospital, getIndicators, scorecardUrl } from "../lib/api";
import ProfileOpps from "../components/ProfileOpps";
import ExportMenu from "../components/ExportMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- formatters ----------
function fmtCurrency(n) {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (!isFinite(v)) return "—";
  if (Math.abs(v) >= 1e9) return `$${(v/1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
}
function fmtPct(n) { if (n === null || n === undefined) return "—"; return `${(Number(n) * 100).toFixed(2)}%`; }
function fmtRatio(n) { if (n === null || n === undefined) return "—"; return Number(n).toFixed(2); }
function fmtInt(n) { if (n === null || n === undefined) return "—"; return Math.round(Number(n)).toLocaleString(); }
function fmtDays(n) { if (n === null || n === undefined) return "—"; return `${Number(n).toFixed(1)} d`; }

function trendColor(curr, prev) {
  if (curr == null || prev == null) return "";
  return curr > prev ? "text-emerald-700" : (curr < prev ? "text-rose-700" : "text-zinc-600");
}

// ---------- Lineage modal ----------
function LineageModal({ open, onClose, indicator, cellName, fiscalYear }) {
  if (!open || !indicator) return null;
  const inputs = indicator.inputs || {};
  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="lineage-modal">
      <div className="bg-white border border-zinc-200 rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Lineage</div>
            <div className="text-xl font-semibold">{cellName} <span className="text-sm font-mono text-zinc-500">· FY{fiscalYear}</span></div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none" data-testid="lineage-close">×</button>
        </div>
        <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
          <div><div className="text-[10px] uppercase font-mono text-zinc-500">Value</div><div className="font-mono mt-1">{indicator.value === null ? "—" : String(indicator.value).slice(0, 60)}</div></div>
          <div><div className="text-[10px] uppercase font-mono text-zinc-500">Unit</div><div className="font-mono mt-1">{indicator.unit || "—"}</div></div>
          <div className="col-span-2"><div className="text-[10px] uppercase font-mono text-zinc-500">Formula</div><div className="font-mono mt-1 text-xs">{indicator.formula || "—"}</div></div>
          {indicator.quality_flag && (
            <div className="col-span-2 bg-amber-50 border border-amber-200 rounded p-2">
              <div className="text-[10px] uppercase font-mono text-amber-800">Quality flag: {indicator.quality_flag}</div>
              <div className="text-xs text-amber-900 mt-1">{indicator.anomaly_reason}</div>
            </div>
          )}
        </div>
        <div className="text-[10px] uppercase font-mono text-zinc-500 mb-2">Inputs (CMS source lineage)</div>
        <table className="w-full text-xs border border-zinc-200">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-2 py-1 font-mono text-zinc-500">name</th>
              <th className="px-2 py-1 font-mono text-zinc-500">wksht</th>
              <th className="px-2 py-1 font-mono text-zinc-500">line</th>
              <th className="px-2 py-1 font-mono text-zinc-500">col</th>
              <th className="px-2 py-1 font-mono text-zinc-500">rpt_rec_num</th>
              <th className="px-2 py-1 font-mono text-zinc-500">raw value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(inputs).map(([k, v]) => (
              <tr key={k} className="border-t border-zinc-100">
                <td className="px-2 py-1 font-mono">{k}</td>
                <td className="px-2 py-1 font-mono">{v?.worksheet || v?.wksht_cd || "—"}</td>
                <td className="px-2 py-1 font-mono">{v?.line || (v?.lines || []).join("+") || "—"}</td>
                <td className="px-2 py-1 font-mono">{v?.column || "—"}</td>
                <td className="px-2 py-1 font-mono">{v?.rpt_rec_num || "—"}</td>
                <td className="px-2 py-1 font-mono">{v?.raw_value === null || v?.raw_value === undefined ? "—" : Number(v.raw_value).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-[10px] font-mono text-zinc-400 mt-4">Source: CMS HCRIS Form 2552-10 (HOSP10) + CMS POS + IRS Form 990 via ProPublica.</div>
      </div>
    </div>
  );
}

function AnomalyBadge({ indicator, testid }) {
  if (!indicator?.anomaly_reason) return null;
  return (
    <span className="inline-block ml-1 text-[10px] text-amber-700 cursor-help" title={indicator.anomaly_reason} data-testid={testid}>
      ⚠
    </span>
  );
}

/**
 * Hover-lineage tooltip. Wraps any numeric cell.
 * Shows: indicator, value, source (worksheet / line / column), raw_value of first input, quality_flag.
 * Click still opens the full LineageModal (handled by parent onClick).
 */
function HoverLineageCell({ indicator, cellName, fy, onClick, children, testid }) {
  const inputs = indicator?.inputs || {};
  const firstInputKey = Object.keys(inputs)[0];
  const firstInput = firstInputKey ? inputs[firstInputKey] : null;
  const tooltipContent = (
    <div className="text-[11px] font-mono leading-relaxed">
      <div className="font-semibold text-emerald-300 mb-1">{cellName} · FY{fy}</div>
      <div>worksheet: <span className="text-zinc-300">{firstInput?.worksheet || firstInput?.wksht_cd || "—"}</span></div>
      <div>line: <span className="text-zinc-300">{firstInput?.line || (firstInput?.lines || []).join("+") || "—"}</span></div>
      <div>column: <span className="text-zinc-300">{firstInput?.column || "—"}</span></div>
      <div>raw_value: <span className="text-zinc-300">{firstInput?.raw_value === null || firstInput?.raw_value === undefined ? "—" : Number(firstInput.raw_value).toLocaleString()}</span></div>
      {indicator?.quality_flag && (
        <div className="mt-1 text-amber-300">⚠ {indicator.quality_flag}</div>
      )}
      <div className="mt-1 text-zinc-400 italic">click cell for full lineage</div>
    </div>
  );
  return (
    <Tippy content={tooltipContent} delay={[300, 50]} placement="top" arrow={true}
           theme="hcris" disabled={!indicator}>
      <td className="text-right px-3 py-2 font-mono text-xs cursor-pointer hover:bg-emerald-50"
          onClick={onClick} data-testid={testid}>
        {children}
      </td>
    </Tippy>
  );
}

// ---------- Payer donut ----------
function PayerDonut({ mix, mode }) {
  const data = (mix?.value || {})[mode] || {};
  const med = data.medicare || 0, mcaid = data.medicaid || 0, other = data.other || 0;
  const total = med + mcaid + other;
  if (total === 0) return <div className="text-xs font-mono text-zinc-500">payer mix unavailable</div>;
  const SIZE = 140, R = 60, C = 2 * Math.PI * R;
  let offset = 0;
  const segs = [
    { v: med, color: "#0a0e1a", label: "Medicare" },
    { v: mcaid, color: "#38a169", label: "Medicaid" },
    { v: other, color: "#94a3b8", label: "Other" },
  ];
  return (
    <div className="flex items-center gap-4">
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <g transform={`translate(${SIZE/2},${SIZE/2})`}>
          <circle r={R} fill="none" stroke="#e4e4e7" strokeWidth="18" />
          {segs.map((s, i) => {
            const f = s.v / total;
            const dash = `${f * C} ${C}`;
            const dashOffset = -offset * C;
            offset += f;
            return <circle key={i} r={R} fill="none" stroke={s.color} strokeWidth="18"
                          strokeDasharray={dash} strokeDashoffset={dashOffset}
                          transform="rotate(-90)" />;
          })}
        </g>
      </svg>
      <div className="text-xs font-mono space-y-1">
        {segs.map(s => (
          <div key={s.label} className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: s.color }} />
            <span className="text-zinc-700">{s.label}</span>
            <span className="text-zinc-500">{((s.v/total)*100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Share modal ----------
function ShareModal({ open, onClose, ccn }) {
  const [expires, setExpires] = useState(7);
  const [allowDownload, setAllowDownload] = useState(true);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;
  const create = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/hospitals/${ccn}/share`,
        { expires_in_days: expires === 0 ? null : expires, allow_download: allowDownload },
        { withCredentials: true });
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      setResult({ ...r.data, public_url: `${baseUrl}/share/${r.data.token}` });
    } catch (e) { setResult({ error: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/40 flex items-center justify-center p-4" onClick={onClose} data-testid="share-modal">
      <div className="bg-white border border-zinc-200 rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-4">
          <div className="text-lg font-semibold">Share scorecard</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none">×</button>
        </div>
        {!result && (
          <>
            <div className="text-xs text-zinc-500 mb-3">Issue a read-only public URL for this hospital. No login required for the viewer.</div>
            <label className="block text-xs mb-2 font-mono text-zinc-700">Expires in
              <select value={expires} onChange={e => setExpires(Number(e.target.value))}
                      className="ml-2 border border-zinc-300 rounded px-2 py-1 text-xs" data-testid="share-expiry">
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={0}>Never</option>
              </select>
            </label>
            <label className="block text-xs mb-3 font-mono text-zinc-700">
              <input type="checkbox" checked={allowDownload} onChange={e => setAllowDownload(e.target.checked)} className="mr-2" data-testid="share-allow-download" />
              Allow PDF download
            </label>
            <button onClick={create} disabled={busy} className="btn-brand w-full text-sm" data-testid="share-create-btn">
              {busy ? "Creating..." : "Create share link"}
            </button>
          </>
        )}
        {result?.error && <div className="text-rose-700 text-sm">{result.error}</div>}
        {result?.token && (
          <div data-testid="share-result">
            <div className="text-xs uppercase font-mono text-zinc-500 mb-1">Public URL</div>
            <input className="w-full border border-zinc-300 rounded px-2 py-2 text-xs font-mono"
                   readOnly value={result.public_url} onFocus={(e) => e.target.select()} data-testid="share-url-input" />
            <div className="text-xs text-zinc-500 mt-2">Expires: {result.expires_at || "never"}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- main page ----------
export default function HospitalDetail() {
  const { ccn } = useParams();
  const [hospital, setHospital] = useState(null);
  const [legacyInd, setLegacyInd] = useState(null);
  const [packByYear, setPackByYear] = useState({});
  const [peerMedian, setPeerMedian] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [donutMode, setDonutMode] = useState("days");
  const [modal, setModal] = useState({ open: false, indicator: null, name: "", fy: null });
  const [shareOpen, setShareOpen] = useState(false);
  const [quality, setQuality] = useState(null);
  const [grade, setGrade] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [opMarginBench, setOpMarginBench] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const h = await getHospital(ccn);
        const ind = await getIndicators(ccn);
        // fetch full pack for each fiscal year in trend
        const trend = ind.indicators?.trend || [];
        const byYear = {};
        await Promise.all(trend.map(async t => {
          const r = await axios.get(`${API}/hospitals/${ccn}/indicators/all?fy=${t.fiscal_year}`, { withCredentials: true });
          byYear[t.fiscal_year] = r.data.indicators || {};
        }));
        if (!cancelled) {
          setHospital(h.hospital);
          setLegacyInd(ind.indicators);
          setPackByYear(byYear);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [ccn]);

  // peer median (operating margin) — fetch from data-quality (cheap)
  useEffect(() => {
    if (!hospital?.pos?.total_beds) return;
    const beds = hospital.pos.total_beds;
    const band = beds < 100 ? "small" : beds < 300 ? "medium" : "large";
    // best-effort: use the operating_margin from latest trend median as proxy
    const trend = legacyInd?.trend || [];
    const margins = trend.map(t => t.operating_margin).filter(v => v != null);
    setPeerMedian({ band, value: margins.length ? margins[margins.length - 1] : null });
  }, [hospital, legacyInd]);

  // Quality scorecard
  useEffect(() => {
    axios.get(`${API}/hospitals/${ccn}/quality`, { withCredentials: true })
      .then(r => setQuality(r.data))
      .catch(() => setQuality(null));
    axios.get(`${API}/hospitals/${ccn}/quality/grade`, { withCredentials: true })
      .then(r => setGrade(r.data))
      .catch(() => setGrade(null));
    axios.get(`${API}/hospitals/${ccn}/peer-cohort`, { withCredentials: true })
      .then(r => setCohort(r.data))
      .catch(() => setCohort(null));
    axios.get(`${API}/hospitals/${ccn}/benchmark?fy=2024&indicator=operating_margin`, { withCredentials: true })
      .then(r => setOpMarginBench(r.data))
      .catch(() => setOpMarginBench(null));
  }, [ccn]);

  if (loading) return <div className="p-8 text-sm text-zinc-500" data-testid="hospital-loading">Loading…</div>;
  if (err) return <div className="p-8 text-sm text-red-700" data-testid="hospital-error">{err}</div>;
  if (!hospital) return null;

  const trend = legacyInd?.trend || [];
  const years = trend.map(t => t.fiscal_year);
  const latestFy = hospital.latest_fy;
  const latestPack = packByYear[latestFy] || {};
  const pos = hospital.pos || {};
  const sys = legacyInd?.system_consolidated;

  // helpers for the cells
  const openLineage = (indicator, name, fy) => setModal({ open: true, indicator, name, fy });

  const finRows = [
    { name: "operating_margin", label: "Operating margin", fmt: fmtPct },
    { name: "total_margin",     label: "Total margin",     fmt: fmtPct },
    { name: "ebitda_margin",    label: "EBITDA margin",    fmt: fmtPct },
    { name: "days_cash_on_hand",label: "Days cash",        fmt: fmtDays },
    { name: "current_ratio",    label: "Current ratio",    fmt: fmtRatio },
    { name: "debt_to_equity",   label: "Debt / equity",    fmt: fmtRatio },
    { name: "cost_per_adjusted_discharge", label: "Cost / adj. discharge", fmt: (v) => v == null ? "—" : `$${Math.round(v).toLocaleString()}` },
  ];
  const utilRows = [
    { name: "alos",                    label: "ALOS (days)",      fmt: (v) => v == null ? "—" : v.toFixed(1) },
    { name: "occupancy_rate",          label: "Occupancy",        fmt: fmtPct },
    { name: "cmi",                     label: "Case Mix Index",   fmt: fmtRatio },
    { name: "wage_index",              label: "Wage Index",       fmt: fmtRatio },
    { name: "fte_per_aob",             label: "FTE / AOB",        fmt: fmtRatio },
    { name: "outpatient_revenue_pct",  label: "Outpatient %",     fmt: fmtPct },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="hospital-profile">
      <Link to="/" className="text-emerald-700 text-xs hover:text-emerald-800" data-testid="back-to-search">&larr; back to search</Link>

      {/* Data freshness ribbon (Phase 3 enhancement) */}
      <div className="mt-3 mb-2 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded text-[11px] font-mono text-zinc-600 flex flex-wrap gap-x-4 gap-y-1"
           data-testid="data-freshness-ribbon">
        <span>Financial: FY{years[0] || "—"}–FY{years[years.length-1] || "—"}</span>
        <span className="text-zinc-400">·</span>
        <span>Quality: {(quality?.hcahps?.[0]?.reporting_period || quality?.mortality_readmission?.[0]?.reporting_period || quality?.hai?.[0]?.reporting_period || "no data").slice(0,40)}</span>
        {sys && <><span className="text-zinc-400">·</span><span>System 990: through TY{sys.tax_year}</span></>}
        <span className="text-zinc-400">·</span>
        <span className="text-zinc-400">CMS HCRIS + POS + Care Compare + IRS 990 (ProPublica)</span>
      </div>

      {/* Section 1: Identity strip */}
      <div className="mt-3" data-testid="profile-identity">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900" data-testid="hospital-legal-name">{hospital.legal_name || "—"}</h1>
          <span className="font-mono text-zinc-600" data-testid="hospital-ccn">CCN {hospital.ccn}</span>
          {grade?.grade && (
            <span data-testid="composite-quality-grade"
                  title={`Composite Quality Grade · ${grade.grade} (GPA ${grade.gpa}) · driving: ${grade.driving_component}\nHCAHPS: ${grade.components.hcahps?.grade || '—'}, HAI: ${grade.components.hai?.grade || '—'}, HVBP: ${grade.components.hvbp?.grade || '—'}${grade.reweighted ? '\n(reweighted — some components null)' : ''}`}
                  className={`text-2xl font-bold px-3 py-1 rounded ${grade.color==='green'?'bg-emerald-600 text-white':grade.color==='amber'?'bg-amber-500 text-white':'bg-rose-600 text-white'}`}>
              {grade.grade}
            </span>
          )}
          {cohort?.fallback_level && (
            <span className="badge badge-muted font-mono text-[10px]" data-testid="peer-cohort-badge"
                  title={`Peer cohort: ${cohort.description} · N=${cohort.member_count} · fallback=${cohort.fallback_level}`}>
              COHORT N={cohort.member_count}
            </span>
          )}
          <span className="badge badge-active">{hospital.status || "active"}</span>
          <span className="badge badge-muted font-mono">FY{latestFy || "—"}</span>
          {hospital.parent_legal_name && (
            <span className="badge badge-muted" data-testid="parent-system-badge">SYS: {hospital.parent_legal_name.slice(0,40)}</span>
          )}
        </div>
        <div className="mt-1 text-sm text-zinc-600">{[hospital.street_address, hospital.city, hospital.state, hospital.zip].filter(Boolean).join(" · ") || "—"}</div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs font-mono">
          <span className="badge badge-muted">Beds: {pos.total_beds ?? "—"}</span>
          <span className="badge badge-muted">{pos.ownership_type ? pos.ownership_type.replace(/_/g," ") : "—"}</span>
          <span className="badge badge-muted">{pos.teaching_status || "—"}</span>
          {pos.rural_urban && <span className="badge badge-muted">{pos.rural_urban}</span>}
        </div>
      </div>

      {/* Section 2: 5-year financial summary */}
      <div className="mt-6 border border-zinc-200 rounded-lg bg-white" data-testid="profile-financial-table">
        <div className="px-3 py-2 border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500">5-year financial summary · click any cell for lineage</div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr><th className="text-left px-3 py-2 text-xs font-mono text-zinc-500">Indicator</th>
              {years.map(y => <th key={y} className="text-right px-3 py-2 text-xs font-mono text-zinc-500">FY{y}</th>)}
            </tr>
          </thead>
          <tbody>
            {finRows.map(row => (
              <tr key={row.name} className="border-t border-zinc-100">
                <td className="px-3 py-2 font-mono text-xs text-zinc-700">{row.label}</td>
                {years.map((y, i) => {
                  const ind = packByYear[y]?.[row.name];
                  const v = ind?.value;
                  const prev = i > 0 ? packByYear[years[i-1]]?.[row.name]?.value : null;
                  return (
                    <HoverLineageCell key={y} indicator={ind} cellName={row.label} fy={y}
                                       onClick={() => openLineage(ind, row.label, y)}
                                       testid={`indicator-cell-${row.name}-fy${y}`}>
                      <span className={trendColor(v, prev)}>{row.fmt(v)}</span>
                      <AnomalyBadge indicator={ind} testid={`anomaly-${row.name}-fy${y}`} />
                    </HoverLineageCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Section 3: 5-year utilization */}
      <div className="mt-6 border border-zinc-200 rounded-lg bg-white" data-testid="profile-utilization-table">
        <div className="px-3 py-2 border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500">5-year utilization</div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr><th className="text-left px-3 py-2 text-xs font-mono text-zinc-500">Indicator</th>
              {years.map(y => <th key={y} className="text-right px-3 py-2 text-xs font-mono text-zinc-500">FY{y}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-zinc-100">
              <td className="px-3 py-2 font-mono text-xs text-zinc-700">Total discharges</td>
              {years.map(y => {
                const t = trend.find(t => t.fiscal_year === y);
                return <td key={y} className="text-right px-3 py-2 font-mono text-xs">{fmtInt(t?.total_discharges)}</td>;
              })}
            </tr>
            {utilRows.map(row => (
              <tr key={row.name} className="border-t border-zinc-100">
                <td className="px-3 py-2 font-mono text-xs text-zinc-700">{row.label}</td>
                {years.map(y => {
                  const ind = packByYear[y]?.[row.name];
                  return (
                    <td key={y} className="text-right px-3 py-2 font-mono text-xs cursor-pointer hover:bg-emerald-50"
                        onClick={() => openLineage(ind, row.label, y)}
                        data-testid={`indicator-cell-${row.name}-fy${y}`}>
                      {row.fmt(ind?.value)}<AnomalyBadge indicator={ind} testid={`anomaly-${row.name}-fy${y}`} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Section 3b — Quality Scorecard (Phase 3 Tier A · Care Compare) */}
      {quality && (
        <div className="mt-6 border border-zinc-200 rounded-lg bg-white" data-testid="profile-quality-scorecard">
          <div className="px-3 py-2 border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500">
            Quality scorecard · CMS Care Compare
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4">
            <div className="border border-zinc-100 rounded p-3 cursor-help"
                 title={`HCAHPS Patient Experience · CMS Care Compare dataset dgck-syfz\nmeasures: ${quality.hcahps?.length || 0}\nstar: ${quality.summary?.hcahps_star || '—'}\nperiod: ${quality.hcahps?.[0]?.reporting_period || '—'}`}
                 data-testid="quality-card-hcahps">
              <div className="text-[10px] uppercase font-mono text-zinc-500">HCAHPS measures</div>
              <div className="text-xl font-mono mt-1">{quality.hcahps?.length || 0}</div>
              {quality.summary?.hcahps_star && (
                <div className="text-xs text-emerald-700 mt-1">{quality.summary.hcahps_star}★ overall</div>
              )}
              <div className="text-[9px] text-zinc-400 mt-2 font-mono">dataset dgck-syfz</div>
            </div>
            <div className="border border-zinc-100 rounded p-3 cursor-help"
                 title={`Mortality / Readmission · CMS Care Compare dataset 9n3s-kdb3\nmeasures: ${quality.mortality_readmission?.length || 0}\nperiod: ${quality.mortality_readmission?.[0]?.reporting_period || '—'}`}
                 data-testid="quality-card-mortality">
              <div className="text-[10px] uppercase font-mono text-zinc-500">Mortality / readmission</div>
              <div className="text-xl font-mono mt-1">{quality.mortality_readmission?.length || 0}</div>
              <div className="text-[10px] font-mono text-zinc-500 mt-1">
                {(quality.mortality_readmission || []).slice(0, 3).map(m => m.measure_name?.replace("READM-30-", "").replace("-HRRP", "").slice(0, 8)).join(" · ")}
              </div>
              <div className="text-[9px] text-zinc-400 mt-2 font-mono">dataset 9n3s-kdb3</div>
            </div>
            <div className="border border-zinc-100 rounded p-3 cursor-help"
                 title={`Healthcare-Associated Infections · CMS Care Compare dataset 77hc-ibv8\nmeasures: ${quality.hai?.length || 0}\nperiod: ${quality.hai?.[0]?.reporting_period || '—'}`}
                 data-testid="quality-card-hai">
              <div className="text-[10px] uppercase font-mono text-zinc-500">HAI infections</div>
              <div className="text-xl font-mono mt-1">{quality.hai?.length || 0}</div>
              <div className="text-[10px] font-mono text-zinc-500 mt-1">
                {(quality.hai || []).slice(0, 1).map(h => h.reporting_period?.slice(0, 25)).join("") || "—"}
              </div>
              <div className="text-[9px] text-zinc-400 mt-2 font-mono">dataset 77hc-ibv8</div>
            </div>
            <div className="border border-zinc-100 rounded p-3 cursor-help"
                 title={`HVBP Total Performance Score · CMS Care Compare dataset ypbt-wvdk\nscore: ${quality.summary?.hvbp_total_score || '—'}`}
                 data-testid="quality-card-hvbp">
              <div className="text-[10px] uppercase font-mono text-zinc-500">HVBP TPS</div>
              <div className="text-xl font-mono mt-1">{quality.summary?.hvbp_total_score ? Number(quality.summary.hvbp_total_score).toFixed(1) : "—"}</div>
              <div className="text-[10px] text-zinc-500 mt-1">total perf score</div>
              <div className="text-[9px] text-zinc-400 mt-2 font-mono">dataset ypbt-wvdk</div>
            </div>
          </div>
          {(quality.hcahps?.length || 0) + (quality.mortality_readmission?.length || 0) + (quality.hai?.length || 0) === 0 && (
            <div className="px-4 pb-4 text-xs text-amber-700">
              ⚠ This facility has no Care Compare quality data — likely a non-acute or government hospital that opts out of voluntary reporting.
            </div>
          )}
          <div className="px-4 pb-3 text-[10px] font-mono text-zinc-400">
            Source: CMS Care Compare provider-data datastore API. Click measure-name → full lineage modal (coming next iteration).
          </div>
        </div>
      )}

      {/* Section 3c — Medicare OPPS outpatient utilization (Phase 4B-5) */}
      <div className="mt-6 border border-zinc-200 rounded-lg bg-white p-4">
        <ProfileOpps ccn={ccn} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Section 4: Payer mix donut */}
        <div className="border border-zinc-200 rounded-lg bg-white p-4" data-testid="profile-payer-donut">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[10px] uppercase font-mono text-zinc-500">Payer mix · FY{latestFy}</div>
            <div className="flex gap-1 text-xs">
              <button className={`px-2 py-1 rounded ${donutMode === "days" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`}
                      onClick={() => setDonutMode("days")} data-testid="donut-days-btn">Days</button>
              <button className={`px-2 py-1 rounded ${donutMode === "discharges" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`}
                      onClick={() => setDonutMode("discharges")} data-testid="donut-disch-btn">Discharges</button>
            </div>
          </div>
          <PayerDonut mix={latestPack.payer_mix} mode={donutMode} />
        </div>

        {/* Section 5: Peer benchmark preview */}
        <div className="border border-zinc-200 rounded-lg bg-white p-4" data-testid="profile-peer-benchmark">
          <div className="text-[10px] uppercase font-mono text-zinc-500 mb-3">Peer benchmark · operating margin</div>
          {(() => {
            const myMargin = latestPack.operating_margin?.value;
            const cohortMedian = peerMedian?.value;
            const max = Math.max(Math.abs(myMargin || 0), Math.abs(cohortMedian || 0), 0.1);
            return (
              <div className="space-y-3">
                <div className="text-xs font-mono text-zinc-500">Cohort: {peerMedian?.band} hospital · {pos.ownership_type ? pos.ownership_type.replace(/_/g," ") : "—"} · {pos.teaching_status || "—"}</div>
                <div>
                  <div className="flex justify-between text-xs font-mono mb-1"><span>This hospital</span><span>{fmtPct(myMargin)}</span></div>
                  <div className="h-3 bg-zinc-100 rounded"><div className="h-3 bg-emerald-600 rounded" style={{ width: `${Math.min(100, ((myMargin || 0) / max) * 100)}%` }} /></div>
                </div>
                <div>
                  <div className="flex justify-between text-xs font-mono mb-1"><span>Peer median (FY{latestFy})</span><span>{fmtPct(cohortMedian)}</span></div>
                  <div className="h-3 bg-zinc-100 rounded"><div className="h-3 bg-zinc-400 rounded" style={{ width: `${Math.min(100, ((cohortMedian || 0) / max) * 100)}%` }} /></div>
                </div>
                <div className="text-[10px] text-zinc-400">Phase 4 will replace this with full HHI + bed-band + state-mix benchmarking.</div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Section 6: Uncompensated care */}
      <div className="mt-6 border border-zinc-200 rounded-lg bg-white p-4" data-testid="profile-uncomp-care">
        <div className="text-[10px] uppercase font-mono text-zinc-500 mb-3">S-10 community benefit · FY{latestFy}</div>
        <div className="grid grid-cols-3 gap-4">
          {[
            { name: "s10_charity_care", label: "Charity care cost" },
            { name: "s10_bad_debt", label: "Bad debt (non-Medicare)" },
            { name: "s10_uncompensated_care", label: "Uncompensated care total" },
          ].map(card => {
            const ind = latestPack[card.name];
            return (
              <div key={card.name} className="border border-zinc-100 rounded p-3 cursor-pointer hover:bg-emerald-50"
                   onClick={() => openLineage(ind, card.label, latestFy)} data-testid={`uncomp-${card.name}`}>
                <div className="text-[10px] uppercase font-mono text-zinc-500">{card.label}</div>
                <div className="text-lg font-mono mt-1">{fmtCurrency(ind?.value)}<AnomalyBadge indicator={ind} /></div>
                <div className="mt-2 flex gap-1 items-end h-8">
                  {years.map(y => {
                    const v = packByYear[y]?.[card.name]?.value || 0;
                    const allVals = years.map(yy => packByYear[yy]?.[card.name]?.value || 0);
                    const mx = Math.max(...allVals, 1);
                    return <div key={y} className="flex-1 bg-emerald-600/70 rounded-t" style={{ height: `${(v/mx)*100}%` }} title={`FY${y}: ${fmtCurrency(v)}`} />;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 7: System context */}
      {sys && (
        <div className="mt-6 border border-emerald-200 bg-emerald-50/40 rounded-lg p-4" data-testid="profile-system-context">
          <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 mb-2">Parent system · IRS Form 990 TY{sys.tax_year}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-lg font-semibold">{sys.parent_legal_name}</div>
              <div className="text-xs font-mono text-zinc-500 mt-1">EIN {sys.parent_ein}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm font-mono">
              <div><div className="text-[10px] uppercase text-zinc-500">System revenue</div><div className="mt-1">{fmtCurrency(sys.total_revenue)}</div></div>
              <div><div className="text-[10px] uppercase text-zinc-500">System margin</div><div className="mt-1">{fmtPct(sys.operating_margin)}</div></div>
              <div><div className="text-[10px] uppercase text-zinc-500">System expenses</div><div className="mt-1">{fmtCurrency(sys.total_expenses)}</div></div>
              <div><div className="text-[10px] uppercase text-zinc-500">CCN op margin</div><div className="mt-1">{fmtPct(latestPack.operating_margin?.value)}</div></div>
            </div>
          </div>
          <div className="text-xs text-emerald-800 mt-3">{sys.note}</div>
        </div>
      )}

      {/* Section 8: action buttons */}
      <div className="mt-6 flex flex-wrap gap-3">
        <a href={scorecardUrl(ccn)} target="_blank" rel="noreferrer" className="btn-brand text-xs" data-testid="profile-pdf-btn">
          Download scorecard (PDF)
        </a>
        {(() => {
          const fyLatest = (hospital && hospital.latest_fy) || (trend && trend[trend.length-1] && trend[trend.length-1].fiscal_year) || 2024;
          const fyq = `?fy=${fyLatest}`;
          return (
            <ExportMenu
              testId="hospital-export"
              items={[
                { label: "Indicator pack", csvUrl: `${API}/hospitals/${ccn}/indicators.csv${fyq}`, xlsxUrl: `${API}/hospitals/${ccn}/indicators.xlsx${fyq}` },
                { label: "Benchmark (12 indicators)", csvUrl: `${API}/hospitals/${ccn}/benchmark.csv${fyq}`, xlsxUrl: `${API}/hospitals/${ccn}/benchmark.xlsx${fyq}` },
                { label: "Peer cohort", csvUrl: `${API}/hospitals/${ccn}/peer-cohort.csv${fyq}`, xlsxUrl: `${API}/hospitals/${ccn}/peer-cohort.xlsx${fyq}` },
                { label: "OPPS utilization", csvUrl: `${API}/hospitals/${ccn}/opps.csv`, xlsxUrl: `${API}/hospitals/${ccn}/opps.xlsx` },
              ]}
            />
          );
        })()}
        <button onClick={() => setShareOpen(true)} className="border border-zinc-300 px-3 py-2 text-xs font-mono rounded hover:bg-zinc-50" data-testid="profile-share-btn">
          Share scorecard →
        </button>
      </div>

      <div className="mt-8 pt-4 border-t border-zinc-200 text-[10px] font-mono text-zinc-400">
        Every number above is sourced directly from CMS HCRIS Form 2552-10 (HOSP10) + CMS Provider of Services + IRS Form 990. Click any cell for full worksheet/line/column lineage with raw_value and rpt_rec_num. Operating margin guardrails: hard-cap ±100%, soft-review ±50%. See /app/docs/INDICATORS.md.
      </div>

      <LineageModal open={modal.open} onClose={() => setModal({ open: false })}
                    indicator={modal.indicator} cellName={modal.name} fiscalYear={modal.fy} />
      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} ccn={ccn} />
    </div>
  );
}
